import message from 'antd/lib/message';
import { eventChannel } from 'redux-saga';
import { call, put, takeLatest, take } from 'redux-saga/effects';
import ChatApi from 'src/api/ChatApi';
import { chatWebSocket } from 'src/api/chatWebSocket';
import {
  actionTypes,
  fetchSessionsSuccess,
  fetchSessionsError,
  createSessionSuccess,
  createSessionError,
  fetchMessagesSuccess,
  fetchMessagesError,
  sendMessageSuccess,
  sendMessageError,
  connectWebSocketSuccess,
  connectWebSocketError,
  disconnectWebSocket,
  addMessage,
  setTyping,
  fetchPreferencesSuccess,
  fetchPreferencesError,
  updatePreferencesSuccess,
  updatePreferencesError
} from './chatAction';

// WebSocket event channel
function createWebSocketChannel(token: string, sessionId: string) {
  return eventChannel((emit) => {
    chatWebSocket.connect(token, sessionId, {
      onMessage: (data) => {
        emit({ type: 'MESSAGE', payload: data });
      },
      onConnected: () => {
        emit({ type: 'CONNECTED' });
      },
      onDisconnected: () => {
        emit({ type: 'DISCONNECTED' });
      },
      onError: (error) => {
        emit({ type: 'ERROR', payload: error });
      }
    });

    // Return unsubscribe function
    return () => {
      chatWebSocket.disconnect();
    };
  });
}

// Fetch sessions
function* fetchSessionsSaga(): any {
  try {
    const sessions = yield call(ChatApi.getSessions);
    yield put(fetchSessionsSuccess(sessions));
  } catch (error: any) {
    yield put(fetchSessionsError(error.message || 'Failed to fetch sessions'));
    message.error('Không thể tải danh sách phiên chat');
  }
}

// Create session
function* createSessionSaga(
  action: ReturnType<typeof import('./chatAction').createSessionRequest>
): any {
  try {
    const { language } = action.payload;
    const session = yield call(ChatApi.createSession, language);
    yield put(createSessionSuccess(session));

    // Auto-connect WebSocket for new session
    const token = localStorage.getItem('token');
    if (token) {
      yield put({
        type: actionTypes.CONNECT_WEBSOCKET_REQUEST,
        payload: { sessionId: session.id }
      });
    }
  } catch (error: any) {
    yield put(createSessionError(error.message || 'Failed to create session'));
    message.error('Không thể tạo phiên chat mới');
  }
}

// Fetch messages
function* fetchMessagesSaga(
  action: ReturnType<typeof import('./chatAction').fetchMessagesRequest>
): any {
  try {
    const { sessionId } = action.payload;
    const messages = yield call(ChatApi.getMessages, sessionId);
    yield put(fetchMessagesSuccess(sessionId, messages));
  } catch (error: any) {
    yield put(fetchMessagesError(error.message || 'Failed to fetch messages'));
    message.error('Không thể tải tin nhắn');
  }
}

// Send message (via REST API)
function* sendMessageSaga(
  action: ReturnType<typeof import('./chatAction').sendMessageRequest>
): any {
  try {
    const { sessionId, content, language, pageContext } = action.payload;

    // Optimistically add user message to UI
    const userMessage = {
      id: `temp-${Date.now()}`,
      chat_session_id: sessionId,
      sender_type: 'user' as const,
      content,
      token_count: 0,
      created_at: new Date().toISOString()
    };
    yield put(addMessage(sessionId, userMessage));

    // Send via WebSocket if connected, otherwise via REST
    if (chatWebSocket.isConnected()) {
      const sent = chatWebSocket.sendMessage(content, language, pageContext);
      if (sent) {
        yield put(sendMessageSuccess(userMessage));
      } else {
        throw new Error('WebSocket not connected');
      }
    } else {
      // Fallback to REST API
      const message = yield call(ChatApi.sendMessage, sessionId, content, language, pageContext);
      yield put(sendMessageSuccess(message));
    }
  } catch (error: any) {
    yield put(sendMessageError(error.message || 'Failed to send message'));
    message.error('Không thể gửi tin nhắn');
  }
}

// WebSocket connection saga
function* connectWebSocketSaga(
  action: ReturnType<typeof import('./chatAction').connectWebSocketRequest>
): any {
  let wsChannel = null;

  try {
    const { sessionId } = action.payload;
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Create WebSocket channel
    wsChannel = yield call(createWebSocketChannel, token, sessionId);
    yield put(connectWebSocketSuccess());

    // Listen to WebSocket events
    while (true) {
      const event = yield take(wsChannel);

      switch (event.type) {
        case 'CONNECTED':
          yield put(connectWebSocketSuccess());
          break;

        case 'DISCONNECTED':
          yield put(disconnectWebSocket());
          break;

        case 'MESSAGE':
          yield call(handleWebSocketMessage, event.payload, sessionId);
          break;

        case 'ERROR':
          yield put(connectWebSocketError(event.payload?.message || 'WebSocket error'));
          break;
      }
    }
  } catch (error: any) {
    yield put(connectWebSocketError(error.message || 'Failed to connect WebSocket'));
    message.error('Không thể kết nối WebSocket');
  } finally {
    if (wsChannel) {
      wsChannel.close();
    }
  }
}

// Note: Streaming chunks logic removed - now receiving complete messages
// Backend accumulates full response before sending to prevent data loss

// Handle WebSocket message
function* handleWebSocketMessage(data: any, sessionId: string) {
  switch (data.type) {
    case 'system':
      // System messages (connection status, etc.) - log but don't display
      console.log('[ChatSaga] System message:', data.message);
      break;

    case 'typing':
      yield put(setTyping(data.typing));
      break;

    case 'message':
      // Full message received
      yield put(addMessage(sessionId, data.message));
      yield put(setTyping(false));
      break;

    case 'message_received':
      // Message acknowledgment from backend - only add if it's NOT the user's own message
      // (user messages are already added optimistically in sendMessageSaga)
      if (data.message && data.message.sender_type !== 'user') {
        yield put(addMessage(sessionId, data.message));
      }
      break;

    case 'streaming_chunk':
      // Streaming chunk received - update existing message
      // This would be handled by updateMessage action
      break;

    case 'streaming_done':
      // Streaming completed
      yield put(setTyping(false));
      break;

    case 'policy_response': {
      // Policy search complete response (non-streaming)
      // Received complete message from backend in one go
      const content = data.content || '';
      const messageId = data.message_id || `temp_${Date.now()}`;
      const query = data.query || '';

      // Validate content is not empty
      if (!content.trim()) {
        console.warn('[ChatSaga] policy_response: Empty content received');
        yield put(setTyping(false));
        break;
      }

      // Create new assistant message with complete content
      yield put(
        addMessage(sessionId, {
          id: messageId,
          chat_session_id: sessionId,
          content: content,
          sender_type: 'assistant',
          token_count: 0,
          created_at: new Date().toISOString(),
          metadata: {
            function: 'search_hr_policy',
            query: query
          }
        })
      );

      // Stop typing indicator
      yield put(setTyping(false));
      break;
    }

    case 'message_chunk':
      // Backend streaming chunk - show typing indicator
      yield put(setTyping(true));
      console.log('[ChatSaga] Message chunk received:', data.content?.substring(0, 50));
      break;

    case 'message_complete':
      // Backend streaming complete - add final message
      if (data.message) {
        yield put(addMessage(sessionId, data.message));
        yield put(setTyping(false));
      }
      break;

    case 'error':
      // Error message from backend
      console.error('[ChatSaga] WebSocket error:', data.message);
      message.error(data.message || 'An error occurred');
      break;

    default:
      console.warn('[ChatSaga] Unknown WebSocket message type:', data.type);
  }
}

// Disconnect WebSocket
function disconnectWebSocketSaga() {
  try {
    chatWebSocket.disconnect();
  } catch (error) {
    console.error('[ChatSaga] Error disconnecting WebSocket:', error);
  }
}

// Fetch preferences
function* fetchPreferencesSaga(): any {
  try {
    const preferences = yield call(ChatApi.getPreferences);
    yield put(fetchPreferencesSuccess(preferences));
  } catch (error: any) {
    yield put(fetchPreferencesError(error.message || 'Failed to fetch preferences'));
  }
}

// Update preferences
function* updatePreferencesSaga(
  action: ReturnType<typeof import('./chatAction').updatePreferencesRequest>
): any {
  try {
    const { preferences } = action.payload;
    const updated = yield call(ChatApi.updatePreferences, preferences);
    yield put(updatePreferencesSuccess(updated));
    message.success('Cập nhật cài đặt thành công');
  } catch (error: any) {
    yield put(updatePreferencesError(error.message || 'Failed to update preferences'));
    message.error('Không thể cập nhật cài đặt');
  }
}

// Root saga
export function* chatSaga() {
  yield takeLatest(actionTypes.FETCH_SESSIONS_REQUEST, fetchSessionsSaga);
  yield takeLatest(actionTypes.CREATE_SESSION_REQUEST, createSessionSaga);
  yield takeLatest(actionTypes.FETCH_MESSAGES_REQUEST, fetchMessagesSaga);
  yield takeLatest(actionTypes.SEND_MESSAGE_REQUEST, sendMessageSaga);
  yield takeLatest(actionTypes.CONNECT_WEBSOCKET_REQUEST, connectWebSocketSaga);
  yield takeLatest(actionTypes.DISCONNECT_WEBSOCKET, disconnectWebSocketSaga);
  yield takeLatest(actionTypes.FETCH_PREFERENCES_REQUEST, fetchPreferencesSaga);
  yield takeLatest(actionTypes.UPDATE_PREFERENCES_REQUEST, updatePreferencesSaga);
}

export default chatSaga;
